<script setup>
/**
 * A player's Squash Levels chip on a row (owner, 3 Nov 2026,
 * SQUASH_LEVELS_CARDS_PLAN.md). Draws what the API sent, decided by
 * squashLevelsChipFor: the level chip for someone who shares, a greyed,
 * dashed, icon only chip for someone who does not (or has no account), never
 * their number, and nothing new where the API sent no status. The dashed
 * border means colour is never the only cue; the tooltip says it in words.
 *
 * `allowed`: the surface's own gate (sport uses ratings, singles row).
 * `requireStatus`: the club member list, which marks only members the API
 * sent the status for.
 */
import {computed} from 'vue';
import SquashLevel from '~/components/players/SquashLevel.vue';
import {squashLevelsChipFor} from '~/composables/useSquashLevelsCards';

const props = defineProps({
  person: {type: Object, default: null},
  allowed: {type: Boolean, default: false},
  requireStatus: {type: Boolean, default: false},
  size: {type: String, default: 'x-small'},
});

// The row's spacing class (ml-2 beside a name) must land on the chip itself:
// on the greyed chip the root is a tooltip, which renders no element of its own,
// so it used to sit right against the name (owner review, 3 Nov 2026).
defineOptions({inheritAttrs: false});

const chip = computed(() => squashLevelsChipFor(props.person, {allowed: props.allowed, requireStatus: props.requireStatus}));
</script>

<template>
  <squash-level v-if="chip?.kind === 'level'" v-bind="$attrs" :size="size" :level="chip.level" :squash-level-id="chip.squashLevelId"/>
  <v-tooltip v-else-if="chip?.kind === 'off'" :text="chip.tooltip" location="top">
    <template #activator="{props: tip}">
      <span v-bind="{...$attrs, ...tip}" class="slc-off" role="img" :aria-label="chip.tooltip" data-testid="squash-levels-off">
        <img src="/images/sl_icon.png" alt="" width="13" height="13" class="slc-off-img"/>
      </span>
    </template>
  </v-tooltip>
</template>

<style scoped>
/* The level chip's shape (SquashLevel.vue, x-small), greyed and dashed. */
.slc-off {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  min-width: 22px;
  padding: 0 4px;
  border-radius: 999px;
  border: 1px dashed rgb(var(--v-theme-textSubtle));
  background: transparent;
  flex: 0 0 auto;
  vertical-align: middle;
}
.slc-off-img { display: block; width: 13px; height: 13px; filter: grayscale(1); opacity: .45; }
</style>
